import { useEffect, useState } from "react";
import { UserPlus, Heart, Link2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Recent activity strip — three columns just below the Story Header.
 * Each column shows the last 5 events of one kind: signups, completed
 * donations, referral-link clicks.
 *
 * Mobile (<768px) stacks the columns; desktop is a 3-wide grid.
 */

function formatRelative(iso) {
  if (!iso) return "";
  const then = typeof iso === "string" ? new Date(iso) : iso;
  const now = new Date();
  const diff = (now - then) / 1000; // seconds
  if (diff < 60) return "זה עתה";
  if (diff < 3600) return `לפני ${Math.floor(diff / 60)} דקות`;
  if (diff < 86400) return `לפני ${Math.floor(diff / 3600)} שעות`;
  if (diff < 86400 * 7) return `לפני ${Math.floor(diff / 86400)} ימים`;
  return then.toLocaleDateString("he-IL", { day: "numeric", month: "short" });
}

function Column({ title, icon: Icon, items, empty, renderItem, testId }) {
  return (
    <div
      className="bg-card/70 rounded-md border border-border/60 p-5"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
        <Icon className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
        {title}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">{empty}</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {items.map((it, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-2 border-b border-border/30 last:border-b-0 pb-2 last:pb-0"
            >
              {renderItem(it)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function RecentActivityPanel() {
  const [data, setData] = useState({
    recent_signups: [],
    recent_donations: [],
    recent_clicks: [],
  });

  useEffect(() => {
    let cancelled = false;
    api
      .adminRecentActivity({ n: 5, include_dummy: false })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled)
          setData({ recent_signups: [], recent_donations: [], recent_clicks: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10"
      data-testid="recent-activity-panel"
    >
      <Column
        title="הצטרפויות אחרונות"
        icon={UserPlus}
        items={data.recent_signups}
        empty="אין הצטרפויות אחרונות"
        testId="recent-signups"
        renderItem={(u) => (
          <>
            <div className="truncate">
              <p className="font-heading text-sm text-foreground truncate">
                {u.name || u.email}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {u.via_referral ? "הפניה" : u.source || "ישיר"}
              </p>
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatRelative(u.created_at)}
            </span>
          </>
        )}
      />
      <Column
        title="תרומות אחרונות"
        icon={Heart}
        items={data.recent_donations}
        empty="אין תרומות אחרונות"
        testId="recent-donations"
        renderItem={(d) => (
          <>
            <div className="truncate">
              <p className="font-heading text-sm text-foreground truncate">
                {d.name || d.email || "תורם אנונימי"}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {d.dedication_type && d.dedication_type !== "none"
                  ? d.dedication_type === "memory"
                    ? "לעילוי נשמת"
                    : d.dedication_type === "honor"
                    ? "להצלחת"
                    : "לרפואת"
                  : d.goal || "—"}
              </p>
            </div>
            <div className="text-right whitespace-nowrap">
              <p className="font-heading text-sm text-foreground">
                {(d.currency || "ILS").toUpperCase() === "USD" ? "$" : "₪"}
                {d.amount}
              </p>
              <p className="text-xs text-muted-foreground">{formatRelative(d.created_at)}</p>
            </div>
          </>
        )}
      />
      <Column
        title="לחיצות הפניה"
        icon={Link2}
        items={data.recent_clicks}
        empty="אין לחיצות אחרונות"
        testId="recent-clicks"
        renderItem={(c) => (
          <>
            <div className="truncate">
              <p className="font-heading text-sm text-foreground truncate">
                קוד: {c.referral_code || "—"}
              </p>
              <p className="text-xs text-muted-foreground truncate" dir="ltr">
                {c.user_agent || "user-agent unknown"}
              </p>
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatRelative(c.created_at)}
            </span>
          </>
        )}
      />
    </section>
  );
}
